import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { fileUrl } from "../../lib/api";

export default function Gallery({ images = [], name }) {
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => setIdx(Math.round(el.scrollLeft / el.clientWidth));
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [images.length]);

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const n = Math.max(0, Math.min(images.length - 1, i));
    el.scrollTo({ left: n * el.clientWidth, behavior: "smooth" });
    setIdx(n);
  };

  if (images.length === 0) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded-2xl border border-hairline bg-slate-100 text-ink/30" data-testid="product-gallery-empty">
        <ImageIcon className="h-12 w-12" />
      </div>
    );
  }

  return (
    <div data-testid="product-gallery">
      <div className="relative overflow-hidden rounded-2xl border border-hairline bg-white">
        <div ref={trackRef} className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="product-gallery-track">
          {images.map((id, i) => (
            <div key={id} className="aspect-square w-full shrink-0 snap-center bg-slate-50">
              <img src={fileUrl(id)} alt={`${name} – bilde ${i + 1}`} className="h-full w-full object-contain" draggable={false} />
            </div>
          ))}
        </div>
        {images.length > 1 && (
          <>
            <button type="button" onClick={() => goTo(idx - 1)} disabled={idx === 0} aria-label="Forrige bilde" className="absolute left-2 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-navy shadow disabled:opacity-30 sm:flex" data-testid="product-gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
            <button type="button" onClick={() => goTo(idx + 1)} disabled={idx === images.length - 1} aria-label="Neste bilde" className="absolute right-2 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-navy shadow disabled:opacity-30 sm:flex" data-testid="product-gallery-next"><ChevronRight className="h-5 w-5" /></button>
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5" data-testid="product-gallery-dots">
              {images.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-electric" : "w-1.5 bg-navy/30"}`} />)}
            </div>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {images.map((id, i) => (
            <button key={id} type="button" onClick={() => goTo(i)} className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${i === idx ? "border-electric" : "border-transparent"}`} data-testid={`product-gallery-thumb-${i}`}>
              <img src={fileUrl(id)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
